/**
 * Labstep
 *
 * @module components/Filter/List
 * @desc A list of filter items
 */

import { FilterItem } from 'labstep-web/components/Filter/Item';
import { ErrorBoundaryContainer } from 'labstep-web/containers/ErrorBoundary';
import { withUiPersistent } from 'labstep-web/containers/UiPersistent';
import { ParamsContext } from 'labstep-web/contexts/params';
import { ParamsStateContext } from 'labstep-web/contexts/params-state';
import { ParamsHOC } from 'labstep-web/hoc/Params';
import { LocationService } from 'labstep-web/services/location.service';
import type React from 'react';
import { withRouter } from 'react-router-dom';
import FilterSectionContent from './Content';
import FilterSectionHeader from './Header';
import type {
  FilterListComponentProps,
  FilterSectionProps,
} from './types';
import { isActive } from './utils';

export const FilterListComponent: React.FC<
  FilterListComponentProps
> = ({ filters, action, useURL, history, location }) => (
  <>
    <ParamsHOC historyAction="replace">
      <ParamsStateContext.Consumer>
        {(paramsStateProps) => (
          <ParamsContext.Consumer>
            {({
              setParams,
              searchParams,
              clearParams,
              clearAll,
              addPostFilter,
            }) => (
              <>
                {filters.map((filter) => {
                  const allParams = {
                    ...searchParams,
                    ...(paramsStateProps.entityView?.parameters ||
                      {}),
                  };
                  const active = isActive(
                    allParams,
                    filter.params,
                    filter.postFilterValues,
                  );
                  return (
                    <FilterItem
                      key={filter.text}
                      onClick={() => {
                        if (useURL && !filter.postFilterValues) {
                          LocationService.addQueryParameters(
                            history,
                            location,
                            filter.params as Record<string, string>,
                          );
                          return;
                        }
                        if (filter.postFilterValues) {
                          const [postFilter, values] =
                            filter.postFilterValues;
                          return addPostFilter(
                            postFilter.getNode(
                              active
                                ? (
                                    values as (string | undefined)[]
                                  ).map(() => undefined)
                                : (values as (string | undefined)[]),
                            ),
                            true,
                          );
                        }
                        return active
                          ? clearParams(Object.keys(filter.params))
                          : setParams(filter.params);
                      }}
                      active={active}
                      text={filter.text}
                      icon={filter.icon}
                      component={filter.component}
                      actionMenu={filter.actionMenu}
                      subFilters={filter.subFilters}
                      useURL={useURL}
                    />
                  );
                })}
              </>
            )}
          </ParamsContext.Consumer>
        )}
      </ParamsStateContext.Consumer>
    </ParamsHOC>
    {action}
  </>
);

export const FilterList = withRouter(FilterListComponent);

export const FilterSectionComponent: RFC<FilterSectionProps> = ({
  filters,
  header,
  extraFilters,
  route,
  children,
  filter,
  useURL,
}) => (
  <ErrorBoundaryContainer>
    <div id={`filter-list-${filter.name}`}>
      <FilterSectionHeader
        header={header}
        route={route}
        filter={filter}
      />
      <FilterSectionContent
        filters={filters}
        filter={filter}
        extraFilters={extraFilters}
        children={children}
        useURL={useURL}
      />
    </div>
  </ErrorBoundaryContainer>
);

export const FilterSection = withUiPersistent(FilterSectionComponent);
